import 'core-js/stable';
import 'regenerator-runtime/runtime';
import React, { Suspense } from 'react';
import { ApolloProvider } from '@common/hooks';
import apollo from 'works/legacy/lib/graph-ql';
import { ErrorBoundary } from './legacy/lib/sentry';
import { loadIntercom } from 'works/legacy/lib/intercom';
import Auth from 'works/legacy/lib/auth';
import {
  AppLayout,
  AppSidebar,
  BrowserRouter,
  ContentSkeleton,
} from '@common/components';
import { AuthGuard } from 'works/legacy/components/auth-guard';
import { OrganizationOnly } from 'works/legacy/components/auth-guard/organization-only';
import { SupplierOnly } from 'works/legacy/components/auth-guard/supplier-only';
import { PublicRoutes } from '@routes/public/public-routes';
import { InactivityModal } from '@identity/components/inactivity-modal';
import { ChunkVersionController } from '@routes/chunk-version-controller';
import { DialogsController } from '@routes/dialogs';
import { SentryMetadataInitializer } from '@identity/components/sentry-metadata-initializer';
import { lazyChunk } from '@routes/services/lazy-chunk-import/lazy-chunk-import';

const importOrganizationRoutes = () =>
  import('@routes/organization/organization-routes');
const importSupplierRoutes = () => import('@routes/supplier/supplier-routes');

// NOTE: these specifiers and the "org-mission-control" chunk name must match the route
// trees' lazy imports; both landings warm because canShowMissionControl is known only after the query.
const warmOrganizationLanding = () => {
  void import('@institutions/pages/home-page/home-page').catch(() => {});
  void import(
    /* webpackChunkName: "org-mission-control" */ '@institutions/pages/mission-control-staffing-page/mission-control-staffing-page'
  ).catch(() => {});
};
const warmSupplierLanding = () => {
  void import('@institutions/pages/home-page/home-page').catch(() => {});
};

const OrganizationRoutes = lazyChunk(
  importOrganizationRoutes,
  'OrganizationRoutes'
);
const SupplierRoutes = lazyChunk(importSupplierRoutes, 'SupplierRoutes');

if (Auth.organizationMember()) {
  void importOrganizationRoutes().catch(() => {});
  warmOrganizationLanding();
} else if (Auth.supplierMember()) {
  void importSupplierRoutes().catch(() => {});
  warmSupplierLanding();
}

export function App() {
  loadIntercom();

  return (
    <React.StrictMode>
      <ErrorBoundary>
        <ApolloProvider client={apollo}>
          <BrowserRouter>
            <ChunkVersionController />
            <PublicRoutes />
            <AuthGuard>
              <SentryMetadataInitializer />
              <DialogsController />
              <AppLayout sidebar={<AppSidebar />}>
                <Suspense fallback={<ContentSkeleton />}>
                  <OrganizationOnly>
                    <OrganizationRoutes />
                  </OrganizationOnly>
                  <SupplierOnly>
                    <SupplierRoutes />
                  </SupplierOnly>
                </Suspense>
              </AppLayout>
              <InactivityModal />
            </AuthGuard>
          </BrowserRouter>
        </ApolloProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
}
